"use client";
import { useState, useEffect, useRef } from "react";
import Image from "next/image";
import { useIsDark } from "@/context/ThemeContext";
import { twMerge } from "tailwind-merge";
import { getCategories, getTags } from "@/sanity/actions/blogActions";

export default function SearchFilter({ onChange, filters }) {
  const isdark = useIsDark();

  const [isDateOpen, setIsDateOpen] = useState(false);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [isTagOpen, setIsTagOpen] = useState(false);

  const publicationOptions = ["Today", "This Week", "This Month", "5 Month"];
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);

  const handleChange = (name, value) => {
    const newFilters = { ...filters, [name]: value };
    onChange?.(newFilters);
  };

  const removeFilter = (filterType) => {
    const newFilters = { ...filters, [filterType]: "" };
    onChange?.(newFilters);
  };

  const clearAllFilters = () => {
    const clearedFilters = {
      date: "",
      category: "",
      tag: "",
      search: "",
    };
    onChange?.(clearedFilters);
  };

  const dateRef = useRef(null);
  const categoryRef = useRef(null);
  const tagRef = useRef(null);

  useEffect(() => {
    async function loadCategoriesAndTags() {
      try {
        const cats = await getCategories();
        const tags = await getTags();
        setCategoryOptions(cats.map((c) => c.title));
        setTagOptions(tags.map((t) => t.title));
      } catch (err) {
        console.error("Error fetching categories and tags:", err);
      }
    }
    loadCategoriesAndTags();
  }, []);

  // Dropdown styles
  const dropdownStyles = twMerge(
    "absolute top-[60px] w-full lg:w-[162px] xl:w-[172px] rounded-md shadow-lg z-100 overflow-y-auto max-h-[266px] thin-scrollbar",
    isdark
      ? "bg-[#181A20] border border-[#818284]"
      : "bg-white border border-[#EAEAEE]"
  );

  const dropdownItemStyles = twMerge(
    "w-full min-h-[40px] 2xl:min-h-[44px] flex items-center py-1 laptop:py-2 px-2 text-[11px] laptop:text-[14px] 2xl:text-[16px] 3xl:text-[18px] font-medium font-[Manrope] leading-[20px] tracking-[0.1px] cursor-pointer transition overflow-hidden whitespace-normal md:whitespace-normal laptop:whitespace-nowrap hover:overflow-visible",
    isdark
      ? "border-t border-r border-[#818284] hover:bg-gray-700 text-white"
      : "border-t border-[#EAEAEE] hover:bg-gray-100 text-[#1A1A1A]"
  );

  const hasDropdownFilters = filters.date || filters.category || filters.tag;

  const hasChips =
    filters.date || filters.category || filters.tag || filters.search;
  const containerHeight = hasChips ? "h-auto  " : "h-auto";

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        dateRef.current &&
        !dateRef.current.contains(event.target) &&
        categoryRef.current &&
        !categoryRef.current.contains(event.target) &&
        tagRef.current &&
        !tagRef.current.contains(event.target)
      ) {
        setIsDateOpen(false);
        setIsCategoryOpen(false);
        setIsTagOpen(false);
      } else {
        if (dateRef.current && !dateRef.current.contains(event.target)) {
          setIsDateOpen(false);
        }
        if (
          categoryRef.current &&
          !categoryRef.current.contains(event.target)
        ) {
          setIsCategoryOpen(false);
        }
        if (tagRef.current && !tagRef.current.contains(event.target)) {
          setIsTagOpen(false);
        }
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div
      className={twMerge(
        "w-full laptop:min-w-[1243px] rounded-[10px] p-[1.5px] max-w-[1800px] mx-auto",
        containerHeight,
        isdark
          ? "bg-gradient-to-r from-[#161616] via-white/15 to-white/15"
          : "bg-gradient-to-r from-[#E28EF3] to-[#EEEEEE]"
      )}
    >
      <div
        className={twMerge(
          "w-full laptop:h-[108px] rounded-[10px] p-3 lg:p-4 xl:p-5 laptop:p-6 py-4",
          isdark ? "bg-[#181A20] text-secondary" : "bg-[#FFFFFF] text-secondary"
        )}
      >
        {/* Search Bar Mobile*/}
        <div className="lg:hidden mb-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={clearAllFilters}
              className={twMerge(
                "flex items-center justify-center gap-2 px-4 py-[8px] h-[40px] rounded-[12px] text-xs xl:text-[14px] laptop:text-[16px] 3xl:text-[18px] font-medium whitespace-nowrap transition-all duration-300 hover:opacity-90 border cursor-pointer",
                isdark
                  ? "border-[#FF1CF7] text-[#FF1CF7] shadow-[0_2px_4px_0_#FF1CF7CC]"
                  : "border-[#FF1CF7] text-[#FF1CF7] shadow-[0_2px_4px_0_#FF1CF7CC]"
              )}
            >
              <Image
                src="/assets/blog/reset.svg"
                alt=""
                width={16}
                height={16}
                className="w-[16px] h-[16px]"
              />
              Reset Filter
            </button>

            <div className="relative flex-1">
              <Image
                src="/assets/blog/searchIcon.svg"
                alt="search"
                width={17.49}
                height={17.49}
                className="absolute left-3 top-1/2 -translate-y-1/2 opacity-70 pointer-events-none"
              />

              <input
                type="text"
                name="search"
                value={filters.search}
                onChange={(e) => handleChange("search", e.target.value)}
                placeholder="search here...."
                className={twMerge(
                  "w-full h-[40px] rounded-[36px] pl-10 pr-4 font-[Manrope] font-medium text-[14px] xl:text-[16px] 3xl:text-[18px] leading-[20px] tracking-[0.1px] align-middle focus:outline-none placeholder:text-gray-500",
                  isdark
                    ? "bg-[#181A20] border border-[#818284] text-secondary"
                    : "bg-[#FFFFFF] border border-[#1A1A1A80] text-secondary"
                )}
              />
            </div>
          </div>
        </div>

        {/* Main Filter */}
        <div className="flex items-center justify-between gap-2 md:gap-2 lg:gap-4 xl:gap-5 laptop:gap-6">
          {/* Publication Date Dropdown */}
          <div
            ref={dateRef}
            className="flex flex-col w-full lg:w-[152px] xl:w-[172px] laptop:w-[172px] 3xl:w-[212px] 5xl:w-[275px] min-w-[69px] relative"
          >
            <label className="text-[10px] sm:text-[10px] lg:text-[12px] laptop:text-[12px] 3xl:text-[18px] font-medium font-[Manrope] leading-[20px] tracking-[0.1px] dark:text-[#818284] text-[#5A5A6E] mb-2">
              Publication
            </label>

            <button
              type="button"
              onClick={() => setIsDateOpen(!isDateOpen)}
              className="flex items-center justify-between 5xl:w-[275px] 3xl:w-[212px] laptop:w-[172px] xl:w-[172px] lg:w-[119px]w-full h-[36px] leading-[24px] px-0 rounded-[6px] text-[12px] sm:text-[14px] md:text-[14px] lg:text-[14px] laptop:text-[14px] 2xl:text-[18px] 3xl:text-[20px] font-medium font-[Manrope] text-foreground leading-[20px] tracking-[0.1px] cursor-pointer"
            >
              <span className="truncate text-left">
                {filters.date || "Publication"}
              </span>
              <Image
                src={
                  isdark
                    ? "/assets/blog/dropdown2.svg"
                    : "/assets/blog/dropdownlight2.svg"
                }
                alt="dropdown"
                width={10}
                height={10}
                className="inline-block ml-2"
              />
            </button>

            {isDateOpen && (
              <div className={twMerge(dropdownStyles, "h-auto")}>
                {publicationOptions.map((option) => (
                  <div
                    key={option}
                    onClick={() => {
                      handleChange("date", option);
                      setIsDateOpen(false);
                    }}
                    className={dropdownItemStyles}
                  >
                    {option}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Divider */}
          <div
            className={`w-[1px] h-[50px] bg-transparent border-l mt-[5px] ml-0 mobile:ml-[10px] mobile:mr-[20px] ${
              isdark ? "border-[#B4B8BD2B]" : "border-[#5A5A6EB2]"
            }`}
          />

          {/* Categories Dropdown */}
          <div
            ref={categoryRef}
            className="flex flex-col w-full md:w-full lg:w-[162px] xl:w-[172px] laptop:w-[172px] 3xl:w-[212px] 5xl:w-[275px] min-w-[69px] relative"
          >
            <label className="text-[10px] sm:text-[10px] lg:text-[12px] laptop:text-[12px] 3xl:text-[18px] font-medium font-[Manrope] leading-[24px] tracking-[0.1px] dark:text-[#818284] text-[#5A5A6E] mb-2">
              Categories
            </label>

            <button
              type="button"
              onClick={() => setIsCategoryOpen(!isCategoryOpen)}
              className="flex items-center justify-between w-full h-[36px] leading-[24px] px-0 rounded-[6px] text-[12px] sm:text-[14px] lg:text-[14px] laptop:text-[14px] 2xl:text-[18px] 3xl:text-[20px] font-medium font-[Manrope] text-foreground leading-[20px] tracking-[0.1px] cursor-pointer"
            >
              <span className="truncate text-left" title={filters.category}>
                {filters.category || "Categories"}
              </span>
              <Image
                src={
                  isdark
                    ? "/assets/blog/dropdown2.svg"
                    : "/assets/blog/dropdownlight2.svg"
                }
                alt="dropdown"
                width={10}
                height={10}
                className="inline-block ml-2"
              />
            </button>

            {isCategoryOpen && (
              <div className={twMerge(dropdownStyles, "h-auto min-w-[172px] w-auto mobile:w-auto sm:w-auto md:w-full lg:w-auto xl:w-auto")}>
                {categoryOptions.map((option) => (
                  <div
                    key={option}
                    onClick={() => {
                      handleChange("category", option);
                      setIsCategoryOpen(false);
                    }}
                    className={dropdownItemStyles}
                    title={option}
                  >
                    {option}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div
            className={`w-[1px] h-[50px] bg-transparent border-l mt-[5px] ml-0 sm:ml-[10px] sm:mr-[20px] ${
              isdark ? "border-[#B4B8BD2B]" : "border-[#5A5A6EB2]"
            }`}
          />

          {/* Tags Dropdown */}
          <div
            ref={tagRef}
            className="flex flex-col w-full 5xl:w-[275px] 3xl:w-[212px] laptop:w-[172px] xl:w-[172px] lg:w-[162px] md:w-full min-w-[69px] relative"
          >
            <label className="text-[10px] sm:text-[10px] lg:text-[12px] laptop:text-[12px] 3xl:text-[18px] font-medium font-[Manrope] leading-[20px] tracking-[0.1px] dark:text-[#818284] text-[#5A5A6E] mb-2">
              Tags
            </label>

            <button
              type="button"
              onClick={() => setIsTagOpen(!isTagOpen)}
              className="flex items-center justify-between w-full h-[36px] leading-[24px] px-0 rounded-[6px] text-[12px] sm:text-[14px] lg:text-[14px] laptop:text-[14px] 2xl:text-[18px] 3xl:text-[20px] font-medium font-[Manrope] text-foreground leading-[20px] tracking-[0.1px] cursor-pointer"
            >
              <span className="truncate text-left">
                {filters.tag || "Tags"}
              </span>
              <Image
                src={
                  isdark
                    ? "/assets/blog/dropdown2.svg"
                    : "/assets/blog/dropdownlight2.svg"
                }
                alt="dropdown"
                width={10}
                height={10}
                className="inline-block ml-2"
              />
            </button>

            {isTagOpen && (
              <div className={twMerge(dropdownStyles, "h-auto xl:w-auto")}>
                {tagOptions.map((option) => (
                  <div
                    key={option}
                    onClick={() => {
                      handleChange("tag", option);
                      setIsTagOpen(false);
                    }}
                    className={dropdownItemStyles}
                  >
                    {option}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Clear All Button */}
          <div className="hidden ml-4 lg:flex flex-col justify-end">
            <button
              type="button"
              onClick={clearAllFilters}
              className={twMerge(
                "flex items-center justify-center gap-2 px-4 py-[8px] h-[40px] rounded-[12px] text-xs xl:text-[14px] laptop:text-[16px] 3xl:text-[18px] font-medium whitespace-nowrap transition-all duration-300 hover:opacity-90 border cursor-pointer",
                isdark
                  ? "border-[#FF1CF7] text-[#FF1CF7] shadow-[0_2px_4px_0_#FF1CF7CC]"
                  : "border-[#FF1CF7] text-[#FF1CF7] shadow-[0_2px_4px_0_#FF1CF7CC]"
              )}
            >
              <Image
                src="/assets/blog/reset.svg"
                alt=""
                width={16}
                height={16}
                className="w-[16px] h-[16px]"
              />

              <span className="hidden lg:inline xl:hidden">Reset</span>
              <span className="hidden xl:inline">Reset Filter</span>
            </button>
          </div>

          {/* Search Bar desktop */}
          <div className="flex-1 items-end justify-end hidden lg:flex">
            <div className="relative lg:w-[280px] w-full h-[40px]">
              <Image
                src="/assets/blog/searchIcon.svg"
                alt="search"
                width={17.49}
                height={17.49}
                className="absolute left-3 top-1/2 -translate-y-1/2 opacity-70 pointer-events-none"
              />

              <input
                type="text"
                name="search"
                value={filters.search}
                onChange={(e) => handleChange("search", e.target.value)}
                placeholder="search here...."
                className={twMerge(
                  "w-full h-[40px] rounded-[36px] pl-10 pr-4 font-[Manrope] font-medium text-xs xl:text-[14px] 2xl:text-[15px] 3xl:text-[19px] leading-[20px] tracking-[0.1px] align-middle focus:outline-none placeholder:text-gray-500",
                  isdark
                    ? "bg-[#181A20] border border-[#818284] text-secondary"
                    : "bg-[#FFFFFF] border border-[#1A1A1A80] text-secondary"
                )}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
